import { useEffect } from 'react';
import { useGameStore, EventBus } from '@campus/core';
import { isPageInBackground, notifyMediaStillOn, onPageBackgroundChange } from '../lib/mediaReminder.js';

/** Délai avant de passer Absent : un aller-retour rapide vers un autre onglet ne change rien. */
export const AUTO_AWAY_DELAY_MS = 30_000;

/**
 * useTabVisibility — statut Absent automatique.
 *
 * Placio n'est plus au premier plan (autre onglet, fenêtre réduite, autre
 * fenêtre ou application au premier plan, écran verrouillé, veille) depuis
 * AUTO_AWAY_DELAY_MS (isPageInBackground) → Absent, affiché aux autres
 * seulement : micro et caméra ne bougent pas (GameBridge.applyStatusMedia,
 * seul un Absent choisi à la main les coupe). Retour sur l'onglet → Disponible.
 *
 * Jamais en conversation (bulle, salle de réunion ou voisins à portée) :
 * changer d'onglet pendant un échange est normal (montrer un document, prendre
 * des notes). Revérifié toutes les AUTO_AWAY_DELAY_MS tant que l'onglet est caché.
 *
 * Seulement depuis Disponible : Occupé et Focus coupent déjà plus, et un
 * Absent choisi à la main (awayReason 'manual') n'est jamais levé tout seul.
 * Jamais pendant un partage d'écran : on partage souvent un autre onglet.
 *
 * Le WebSocket reste ouvert dans un onglet en arrière-plan : le statut part
 * tout de suite. En veille, la connexion tombe et la reconnexion renvoie le
 * statut courant (ColyseusClient.connect).
 */
export function useTabVisibility() {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const goAway = () => {
      timer = null;
      const s = useGameStore.getState();
      if (s.localStatus !== 'available' || s.isScreenSharing) return;
      if (!isPageInBackground()) {
        // Focus dans une iframe de la page (app intégrée) : si la personne passe
        // ensuite à une autre fenêtre, la page n'en est pas prévenue. On revérifie.
        if (document.activeElement instanceof HTMLIFrameElement) timer = setTimeout(goAway, AUTO_AWAY_DELAY_MS);
        return;
      }
      // En conversation : on reste Disponible, nouvel essai plus tard.
      if (s.proximityCount > 0) {
        timer = setTimeout(goAway, AUTO_AWAY_DELAY_MS);
        return;
      }
      s.setAwayReason('tab-hidden');
      EventBus.emit('ui:status-change', { status: 'away' });
      // Micro ou caméra restés allumés (l'absence automatique ne coupe rien) : prévenir.
      notifyMediaStillOn();
    };

    const onBackgroundChange = () => {
      clearTimer();
      if (isPageInBackground() || document.activeElement instanceof HTMLIFrameElement) {
        timer = setTimeout(goAway, AUTO_AWAY_DELAY_MS);
        return;
      }
      const s = useGameStore.getState();
      if (s.awayReason === 'tab-hidden') {
        s.setAwayReason(null);
        if (s.localStatus === 'away') EventBus.emit('ui:status-change', { status: 'available' });
      }
    };

    const unlisten = onPageBackgroundChange(onBackgroundChange);
    return () => {
      unlisten();
      clearTimer();
    };
  }, []);
}
